import {AfterViewInit, Component, ElementRef, inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {LobbyStore} from "../../../store/lobby.store";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {GameStateStore} from "../../../store/game-state.store";
import {TeamGuessResultComponent} from "./team-guess-result/team-guess-result.component";
import {listAnimation} from "../../../shared/animations/list.animation";
import {TeamGuessResult} from "../../../../backend/messages/guess-result.message";
import {BackdropClassStep, HideDialogStep, Sequence} from "../../../shared/util/sequence.util";
import {Router} from "@angular/router";
import {AbstractMessageListenerService} from "../../../messaging/abstract-message-listener.service";
import {isHost} from "../../../shared/util/window.util";
import {Hit} from "../../../../backend/model/hit.model";
import {guessOptionLabel, OPTION_GUESS_TYPES} from "../../game-guess/guess-option-label";
import {GuessType} from "../../../../backend/state/game-state.model";

export interface GuessResultDialogData {
  results: TeamGuessResult[];
  activeTeamResult?: TeamGuessResult;
  /** Revealed song, defaults to the active hit of the game */
  hit?: Pick<Hit, 'artist' | 'name' | 'year'>;
  /** Whether the guess was correct, defaults to the result of the active team */
  correct?: boolean;
}

export const openGuessResultDialog = (dialog: MatDialog, data: GuessResultDialogData) =>
  dialog.open(GuessResultDialogComponent, {
    hasBackdrop: true,
    backdropClass: ['mat-app-background'],
    panelClass: ['fullscreen', 'transparent', 'animate__animated', 'animate__bounceInDown', 'animate__faster'],
    data
  });

@Component({
  selector: 'app-guess-result-dialog',
  imports: [
    TranslateModule,
    TeamGuessResultComponent
  ],
  templateUrl: './guess-result-dialog.component.html',
  styleUrl: './guess-result-dialog.component.scss',
  animations: [listAnimation]
})
export class GuessResultDialogComponent extends AbstractMessageListenerService<never> implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  @ViewChild('result') resultContainer!: ElementRef;

  readonly gameStore = inject(GameStateStore);

  readonly lobbyStore = inject(LobbyStore);

  readonly data = inject(MAT_DIALOG_DATA) as GuessResultDialogData;

  activeHit = this.data.hit ?? this.gameStore.activeHit();

  correct = this.data.correct ?? this.data.activeTeamResult?.correct;

  showCorrect = true;

  /** Question of the round, shown with the correct answer for option based guess types */
  guessType?: GuessType = this.data.hit ? undefined : this.gameStore.guessType();

  guessTitleParams = this.gameStore.guessTitleParams();

  correctAnswer = this.computeCorrectAnswer();

  translateProperties = {};

  constructor(private ref: MatDialogRef<any>) {
    super('roundBegin')
    const hit = this.gameStore.activeHit();
    if (hit) {
      this.translateProperties = {
        ...hit
      }
    }
  }

  ngAfterViewInit() {
    const className = this.correct ? 'success' : 'failure';
    const backdropStep = isHost() || this.data.activeTeamResult?.skipped ? 0 : new BackdropClassStep(className);
    new Sequence([
      backdropStep,
      8000,
      new BackdropClassStep(className, false),
      new HideDialogStep(this.ref)
    ]).execute();
  }

  private computeCorrectAnswer(): string | undefined {
    const type = this.guessType;
    const option = this.gameStore.correctGuessOption();
    if (!type || !option || !OPTION_GUESS_TYPES.includes(type)) {
      return undefined;
    }
    return guessOptionLabel(type, option, this.translate.currentLang || this.translate.defaultLang || 'en',
      key => this.translate.instant(key));
  }

  close() {
    this.ref.close();
  }

  public override process(message: never): void {
    this.close();
  }
}
